import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import Editor from '@toast-ui/editor';

@Component({
  selector: 'app-markdown-editor',
  templateUrl: './markdown-editor.component.html',
  styleUrls: ['./markdown-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MarkdownEditorComponent),
      multi: true
    }
  ],
  imports:[CommonModule, FormsModule]
})
export class MarkdownEditorComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {

  @ViewChild('editorContainer') editorContainer!: ElementRef;
  private editor!: Editor

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  protected value: string = '';

  @Input() disabled: boolean = false;

  ngAfterViewInit(): void {

    if (this.disabled) {
      return;
    }

    this.editor = new Editor({
      el: this.editorContainer?.nativeElement,
      initialEditType: 'wysiwyg',
      previewStyle: 'vertical',
      height: '200px',
      hideModeSwitch: true, // 🔹 Hide the Markdown tab
      initialValue: this.convertStringToMarkDown(this.value),
      toolbarItems: [
        ['heading', 'bold', 'italic', 'strike'], // Formatting
        ['hr', 'quote'], // Blocks
        ['ul', 'ol'], // Lists
        ['link']
      ]
    });

    // Apply the bug fix for the link popup
    this.setupLinkPopupFix();

    this.editor.on('change', () => {
      const markdown = this.convertMarkDownToValidString(this.editor.getMarkdown());
      this.value = markdown;
      this.onChange(markdown);
    });
  
    this.editor.on('blur', () => {
      this.onTouched();
    });   
  }


 private setupLinkPopupFix(): void {
    // Remove existing 'query' event handlers if any
    // Note: `removeEventHandlerWithTypeInfo` might not be available in all versions; adjust as needed
    if (this.editor.eventEmitter['removeEventHandlerWithTypeInfo']) {
      this.editor.eventEmitter['removeEventHandlerWithTypeInfo']('query');
    }

    // Add the custom 'query' event listener for link popup
    this.editor.eventEmitter.listen('query', (query: string, payload:any = {}) => {

      if (query === 'getPopupInitialValues' && payload.popupName === 'link') {
        const range = this.editor.getSelection();
        const info = this.editor.getRangeInfoOfNode(Math.floor((range[0] + range[1]) / 2));
        
        if (info.type === 'link') {
          this.editor.setSelection(...info.range);
          const selection = window.getSelection()!;
          if (selection.rangeCount > 0) {
            let link = selection.getRangeAt(0).commonAncestorContainer.parentElement as HTMLAnchorElement;
            link = link.closest('a') || link.querySelector('a') || link;
            return {
              linkUrl: link.href || '',
              linkText: link.innerText || '',
            };
          }
        } else {
          return {
            linkText: this.editor.getSelectedText(),
          };
        }
      }
      return {}; // Default return if conditions aren't met
    });
  }


  writeValue(value: string): void {
    this.value = this.convertStringToMarkDown(value || '');
    if (this.editor) {
      this.editor.setMarkdown(this.value);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }


  ngOnDestroy(): void {
    this.editor?.destroy();
  }
  
  convertMarkDownToValidString(markdown: string): string {
    return markdown
      .trim() // Remove unnecessary spaces
      .replace(/\n{2,}/g, '\\n\\n') // Ensure paragraph breaks are stored as \n\n
      .replace(/(\n|^) *([-*] .+)/g, '\\r$2') // Ensure bullet list items start with \r
      .replace(/(\n|^) *(\d+\. .+)/g, '\\r$2'); // Ensure numbered list items start with \r
  }
  
  convertStringToMarkDown(text: string): string {
    return text
      .trim() // Remove unnecessary spaces
      .replace(/\\n\\n/g, '\n\n') // Convert stored \n\n back to real new lines
      .replace(/\\r/g, '\n'); // Convert stored \r back to real new lines for lists
  }

  
}
